import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useHistory } from "react-router";
import { fetchWorkspaceStats } from "../../actions/stats";
import styled from "styled-components";
import KpiPageCrawledTile from "../TrendsDashboard/KpiPageCrawledTile";
import KpiErrorsTile from "../TrendsDashboard/KpiErrorsTile";
import KpiIndexabilityTile from "../TrendsDashboard/KpiIndexabilityTile";
import KpiRecommendationTile from "../TrendsDashboard/KpiRecommendationTile";

// Dashboard tile sparklines show the last N snapshots — a count, not a calendar window —
// so the chart shape doesn't depend on how often the workspace is crawled. Date-range
// exploration belongs on /trends; the dashboard is glance-and-go.
const TILE_LAST_N = 30;

const C = {
    surface:    "#ffffff",
    border:     "#e2e8f0",
    track:      "#eef2f6",
    rowHover:   "#f8fafc",
    text:       "#0f172a",
    subtle:     "#475569",
    muted:      "#94a3b8",
    blue:       "#2563eb",
    amber:      "#b45309",
    green:      "#15803d",
};

const DashGrid = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 16px;
`;

const KpiRow = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 12px;
    @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
    @media (max-width: 600px) { grid-template-columns: 1fr; gap: 10px; }
`;

const Panel = styled.div`
    background: ${C.surface};
    border: 1px solid ${C.border};
    border-radius: 12px;
    padding: 16px 20px;
    @media (max-width: 600px) {
        border-radius: 0;
        border-left: none;
        border-right: none;
    }
`;

const PanelHead = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    margin-bottom: 14px;
    gap: 12px;
    flex-wrap: wrap;
`;

const PanelLabel = styled.div`
    font-size: 0.62rem;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: ${C.muted};
`;

const PanelMeta = styled.div`
    font-size: 0.72rem;
    color: ${C.subtle};
    font-variant-numeric: tabular-nums;
    display: flex;
    gap: 14px;
`;

const MetaItem = styled.span`
    cursor: pointer;
    transition: color 120ms;
    & b { color: ${C.text}; font-weight: 700; margin-left: 4px; }
    &:hover { color: ${C.text}; }
    &:hover b { color: ${C.blue}; }
`;

const StackedBar = styled.div`
    display: flex;
    height: 14px;
    border-radius: 7px;
    overflow: hidden;
    background: ${C.track};
    margin-bottom: 14px;
`;

const Seg = styled.div`
    height: 100%;
    flex: ${({ $count }) => $count} 0 0;
    min-width: ${({ $count }) => ($count > 0 ? 3 : 0)}px;
    background: ${({ $color }) => $color};
    cursor: ${({ $count }) => ($count > 0 ? "pointer" : "default")};
    transition: opacity 120ms;
    &:hover { opacity: ${({ $count }) => ($count > 0 ? 0.75 : 1)}; }
`;

const LegendRow = styled.div`
    display: grid;
    grid-template-columns: repeat(5, 1fr);
    gap: 6px;
    @media (max-width: 600px) { grid-template-columns: repeat(2, 1fr); }
`;

const LegendItem = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    cursor: pointer;
    transition: background 120ms;
    &:hover { background: ${C.rowHover}; }
`;

const LegendDot = styled.span`
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: ${({ $color }) => $color};
    flex-shrink: 0;
`;

const LegendLabel = styled.span`
    font-size: 0.75rem;
    color: ${C.subtle};
    font-weight: 500;
`;

const LegendCount = styled.span`
    font-size: 0.75rem;
    color: ${({ $zero }) => ($zero ? C.muted : C.text)};
    font-weight: 700;
    margin-left: auto;
    font-variant-numeric: tabular-nums;
`;

const TwoCol = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
    @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const ListRow = styled.div`
    display: grid;
    grid-template-columns: ${({ $bar }) => ($bar ? "1fr 80px 56px" : "1fr 56px")};
    align-items: center;
    gap: 12px;
    padding: 10px 20px;
    margin: 0 -20px;
    cursor: pointer;
    border-top: 1px solid ${C.border};
    transition: background 120ms;
    &:first-of-type { border-top: none; }
    &:hover { background: ${C.rowHover}; }
`;

const RowLabel = styled.div`
    font-size: 0.82rem;
    color: ${C.subtle};
    font-weight: 500;
`;

const RowCount = styled.div`
    font-size: 0.9rem;
    font-weight: 700;
    color: ${({ $zero, $tone }) => ($zero ? C.muted : ($tone || C.text))};
    font-variant-numeric: tabular-nums;
    text-align: right;
`;

const Bar = styled.div`
    height: 4px;
    border-radius: 99px;
    background: ${C.track};
    overflow: hidden;
`;

const BarFill = styled.div`
    height: 100%;
    width: ${({ $pct }) => $pct}%;
    background: ${({ $done }) => ($done ? C.green : C.amber)};
    border-radius: 99px;
    transition: width 400ms ease;
`;

const n = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);
const fmt = (v) => (typeof v === "number" ? v.toLocaleString() : "—");

export default function DashboardSimple({ onFilter }) {
    const dispatch = useDispatch();
    const history  = useHistory();
    const { workspaceNumber } = useParams();

    const stats = useSelector((s) => s.stats?.workspaceStats);

    useEffect(() => {
        dispatch(fetchWorkspaceStats(workspaceNumber));
    }, [dispatch, workspaceNumber]);

    const filter = (qs = "") => onFilter ? onFilter(qs) : history.push(qs || { search: "" });
    const goToTrends = () => history.push(`/ws/${workspaceNumber}/trends`);

    const sc  = stats?.statusCodeStats    || {};
    const rs  = stats?.recommendationStats || {};
    const ix  = stats?.indexabilityStats   || {};
    const csc = stats?.crawlScopeStats     || {};

    const statusItems = [
        { label: "200 OK", count: n(sc.just200Count),  color: "#22c55e",  qs: "?redirectChain=200" },
        { label: "301",    count: n(sc.http301Count),  color: "#3b82f6",  qs: "?httpStatus=301" },
        { label: "3xx",    count: n(sc.http3xxCount),  color: "#93c5fd",  qs: "?httpStatus=300,302,303,304,307" },
        { label: "4xx",    count: n(sc.http4xxCount),  color: "#f87171",  qs: "?httpStatus=400,401,403,404" },
        { label: "5xx",    count: n(sc.http5xxCount),  color: "#b91c1c",  qs: "?httpStatus=500,501,503,504" },
    ];

    const recItems = [
        { label: "Title",    rec: n(rs.recommendedTitleCount),            imp: n(rs.implementedTitleCount),            tag: "Title" },
        { label: "H1",       rec: n(rs.recommendedH1sCount),              imp: n(rs.implementedH1sCount),              tag: "H1" },
        { label: "Meta",     rec: n(rs.recommendedMetaDescriptionsCount), imp: n(rs.implementedMetaDescriptionsCount), tag: "Meta" },
        { label: "Redirect", rec: n(rs.recommendedDestinationUriCount),   imp: n(rs.implementedDestinationUriCount),   tag: "Redirect" },
    ];

    const ixItems = [
        { label: "Meta noindex",     count: n(ix.metaNoIndex),        qs: "?notIndexableReasons=META_NOINDEX" },
        { label: "Canonicalized",    count: n(ix.canonicalized),      qs: "?notIndexableReasons=CANONICALIZED" },
        { label: "Robots blocked",   count: n(ix.blockedByRobotsTxt), qs: "?notIndexableReasons=BLOCKED_BY_ROBOTS_TXT" },
        { label: "X-Robots noindex", count: n(ix.xRobotsNoIndex),     qs: "?notIndexableReasons=X_ROBOTS_NOINDEX" },
    ];

    return (
        <DashGrid>
            <KpiRow>
                <KpiPageCrawledTile lastN={TILE_LAST_N}
                    onFilter={() => filter("")} onInvestigate={goToTrends} />
                <KpiErrorsTile lastN={TILE_LAST_N}
                    onFilter={() => filter("?httpStatus=400,401,403,404,500,501,503,504")} onInvestigate={goToTrends} />
                <KpiIndexabilityTile lastN={TILE_LAST_N}
                    onFilter={() => filter("?notIndexableReasons=META_NOINDEX,CANONICALIZED,BLOCKED_BY_ROBOTS_TXT,X_ROBOTS_NOINDEX")} onInvestigate={goToTrends} />
                <KpiRecommendationTile lastN={TILE_LAST_N}
                    onFilter={() => filter("?diffs=Title,H1,Meta,Redirect")} onInvestigate={goToTrends} />
            </KpiRow>

            <Panel>
                <PanelHead>
                    <PanelLabel>HTTP status</PanelLabel>
                    <PanelMeta>
                        <MetaItem onClick={() => filter("?crawlScope=internal")}>Internal<b>{fmt(n(csc.internal))}</b></MetaItem>
                        <MetaItem onClick={() => filter("?crawlScope=external")}>External<b>{fmt(n(csc.external))}</b></MetaItem>
                    </PanelMeta>
                </PanelHead>
                <StackedBar>
                    {statusItems.map((s) => (
                        <Seg key={s.label} $count={s.count} $color={s.color}
                             onClick={() => s.count > 0 && filter(s.qs)} title={`${s.label}: ${s.count.toLocaleString()}`} />
                    ))}
                </StackedBar>
                <LegendRow>
                    {statusItems.map((s) => (
                        <LegendItem key={s.label} onClick={() => filter(s.qs)}>
                            <LegendDot $color={s.color} />
                            <LegendLabel>{s.label}</LegendLabel>
                            <LegendCount $zero={s.count === 0}>{fmt(s.count)}</LegendCount>
                        </LegendItem>
                    ))}
                </LegendRow>
            </Panel>

            <TwoCol>
                <Panel>
                    <PanelHead><PanelLabel>Indexability issues</PanelLabel></PanelHead>
                    {ixItems.map(({ label, count, qs }) => (
                        <ListRow key={label} onClick={() => filter(qs)}>
                            <RowLabel>{label}</RowLabel>
                            <RowCount $zero={count === 0} $tone={count > 0 ? C.amber : null}>{fmt(count)}</RowCount>
                        </ListRow>
                    ))}
                </Panel>

                <Panel>
                    <PanelHead><PanelLabel>Recommendations</PanelLabel></PanelHead>
                    {recItems.map(({ label, rec, imp, tag }) => {
                        const pending = Math.max(0, rec - imp);
                        const pct     = rec > 0 ? Math.round((imp / rec) * 100) : 100;
                        const done    = pending === 0;
                        return (
                            <ListRow key={tag} $bar onClick={() => filter(`?diffs=${tag}`)}>
                                <RowLabel>{label}</RowLabel>
                                <Bar><BarFill $pct={pct} $done={done} /></Bar>
                                <RowCount $zero={done} $tone={done ? null : C.amber}>{pending}</RowCount>
                            </ListRow>
                        );
                    })}
                </Panel>
            </TwoCol>
        </DashGrid>
    );
}
